"use client";

import { RefObject, useEffect } from "react";

/**
 * Re-arms autoplay for media inside CMS content rendered through
 * dangerouslySetInnerHTML.
 *
 * React builds a newly mounted subtree detached from the document: it creates
 * the container, applies innerHTML, and only appends it during the commit
 * phase. A <video autoplay> created that way starts its media load algorithm
 * while it is not in a document, and the insertion steps do not re-invoke that
 * algorithm — they only do so while networkState is still NETWORK_EMPTY, which
 * it no longer is. The autoplay opportunity is therefore never granted and the
 * element sits at readyState 4, paused, showing its poster.
 *
 * On a hard load the document parser creates the element already inside the
 * document, so autoplay works — which is why this only breaks on client-side
 * navigation. Measured in Chrome with the same markup: innerHTML on a detached
 * container stays paused (readyState 4, played 0), while appending the
 * container first and setting innerHTML afterwards autoplays normally. It is
 * the same family as the inert-<script> problem useReplayCmsScripts solves:
 * work the native parser does for us on a hard load that innerHTML does not.
 *
 * CmsStaticPage is unaffected: it assigns el.innerHTML inside an effect, when
 * the container is already connected.
 *
 * play() is used rather than load(): it is enough (measured), it does not
 * re-fetch the resource or reset currentTime, and it is valid at any
 * readyState. Waiting for `canplay` instead would reintroduce exactly the
 * class of bug this exists to fix — the event may already have fired.
 */
export function useAutoplayCmsMedia(
  containerRef: RefObject<HTMLElement | null>,
  content: string,
): void {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const media = Array.from(
      el.querySelectorAll<HTMLMediaElement>("video[autoplay], audio[autoplay]"),
    );

    for (const item of media) {
      // Already playing — the hydration path, where the parser-created element
      // got its autoplay honoured. Nothing to do, and calling play() would be
      // pointless noise.
      if (!item.paused) continue;

      // Autoplay with sound is denied by the browser policy without a user
      // gesture, exactly as it is on a hard load: the rejection is expected
      // and there is nothing actionable to report.
      item.play().catch(() => undefined);
    }
  }, [content, containerRef]);
}
